<template>
  <pui-flex class="creation-cart creation-cart__root" direction="column">
    <pui-expand-height>
      <!-- wrapper div so transition doesnt cut poster-validation-alert padding -->
      <div v-if="nbSignageItemsInvalid > 0">
        <poster-validation-alert>
          <p>
            {{
              $tc('poster.creation.cart.invalid_count', nbSignageItemsInvalid, {
                count: nbSignageItemsInvalid,
              })
            }}
          </p>
        </poster-validation-alert>
      </div>
    </pui-expand-height>

    <div v-scrollable.y>
      <transition-group name="creation-cart__items-transition-wrapper">
        <div
          ref="temp_type"
          key="temp_type"
          v-if="showTempPosterTypeZone || tempPosterType"
          :class="{
            'creation-cart__item tempType': true,
          }"
        >
          <div class="item-content">
            <p>
              <i18n v-if="tempPosterType" path="poster.creation.cart.item.type.main">
                <template #label>
                  <span>{{ $t('poster.creation.cart.item.type.label') }}</span>
                </template>
                <template #type>
                  {{ tempPosterType | piivoTranslate }}
                </template>
              </i18n>
              <i18n v-else path="poster.creation.cart.item.select_type"></i18n>
            </p>
          </div>
        </div>
        <div
          @mouseenter="hoverPosterId = signageItem.id"
          @mouseleave="hoverPosterId = null"
          v-for="(signageItem, index) in signageItems"
          :ref="`cartItem_${index}`"
          :key="signageItem.id"
          :class="{
            'creation-cart__item': true,
            actif: signageItem === currentSignageItem,
            error: signageItem.isValid === false,
            selected: !!selectedPostersMap[signageItem.id],
            unread: signageItem.unread,
            disabled,
            overlay:
              disabled && (!isChangingCurrentType || signageItem.id !== currentSignageItem.id),
          }"
          @click="$emit('onSignageItemClick', signageItem, $event, false)"
          @keydown.up.prevent
          @keydown.down.prevent
          @keyup.up.prevent="$emit('cartMoveUp', $event)"
          @keyup.down.prevent="$emit('cartMoveDown', $event)"
          tabindex="0"
        >
          <div class="item-leftbar" />
          <pui-expand-width overflowMode="visible">
            <!-- Wrapper div to preserve item-checkbox-container padding during transition -->
            <div v-if="hoverPosterId === signageItem.id || !!selectedPostersMap[signageItem.id]">
              <div class="item-checkbox-container">
                <pui-checkbox
                  :checked="!!selectedPostersMap[signageItem.id]"
                  @click.stop="$emit('onSignageItemClick', signageItem, $event, true)"
                  :boxColorChecked="
                    signageItem === currentSignageItem
                      ? 'var(--color-initial)'
                      : 'var(--color-primary)'
                  "
                  :checkMarkColor="
                    signageItem === currentSignageItem
                      ? 'var(--color-primary)'
                      : 'var(--color-initial)'
                  "
                ></pui-checkbox>
              </div>
            </div>
          </pui-expand-width>
          <div class="item-content">
            <p v-for="inputSummary in signageItem.inputsSummary" :key="inputSummary.alias">
              <i18n path="poster.creation.cart.item.input">
                <template #label>
                  <span>
                    {{ inputSummary.label }}
                  </span>
                </template>
                <template #value>
                  {{ inputSummary.value }}
                </template>
              </i18n>
            </p>
            <p>
              <i18n path="poster.creation.cart.item.type.main">
                <template #label>
                  <span>{{ $t('poster.creation.cart.item.type.label') }}</span>
                </template>
                <template #type>
                  {{
                    (signageItem._tempType ? signageItem._tempType : signageItem.type)
                      | piivoTranslate
                  }}
                </template>
              </i18n>
            </p>
            <p>{{ copiesSummary(signageItem) }}</p>
          </div>
          <div class="item-action">
            <i
              v-track:click="Tracking.QUICK_DELETE_SIGNAGE"
              @click.stop="$emit('deleteSignageItems', [signageItem])"
              class="mdi mdi-delete btn-delete"
            />
          </div>
        </div>
      </transition-group>
    </div>
  </pui-flex>
</template>

<script>
import { getFormattingManager } from 'piivo-poster-engine/src/lib/services/formattingManager';

import ConstantsMixin from '../../mixins/ConstantsMixin';

export default {
  name: 'poster-creation-cart',
  mixins: [ConstantsMixin],
  props: {
    /**
     * Number of invalid item
     */
    nbSignageItemsInvalid: {
      type: Number,
      required: true,
    },
    /**
     * All the items in the cart
     */
    signageItems: {
      type: Array,
      required: true,
    },
    /**
     * The current signage item object
     */
    currentSignageItem: {
      type: null,
      default: () => null,
    },
    /**
     * The selected signage item objects
     */
    selectedSignageItems: {
      type: Array,
      required: true,
    },
    /**
     * Disables moving in the cart or changing the selection
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Temporary poster type to display as a poster
     */
    tempPosterType: {
      type: Object,
      default: null,
    },
    /**
     * If the zone indicating to select a poster type should be shown
     */
    showTempPosterTypeZone: {
      type: Boolean,
      default: false,
    },
    /**
     * If the current poster's type is being changed (selector is open)
     */
    isChangingCurrentType: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      hoverPosterId: null,
    };
  },
  computed: {
    /**
     * @returns {object} object map of selected item ids
     */
    selectedPostersMap() {
      return this.selectedSignageItems.reduce((map, poster) => {
        map[poster.id] = true;
        return map;
      }, {});
    },
  },
  methods: {
    /**
     * Computes the copies summary for the signage item
     *
     * @param {object} signageItem - the poster with the formatsCopiesMap
     * @returns {string} the copies summary
     */
    copiesSummary(signageItem) {
      return getFormattingManager().getFormatter('FormatsCopies')(signageItem.formatsCopiesMap);
    },
    /**
     * Focuses the temp poster zone
     *
     * @returns {boolean} if the item was focused
     */
    focusTempPoster() {
      if (this.$refs.temp_type) {
        this.$refs.temp_type.focus({ preventScroll: true });
        this.$refs.temp_type.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
        return true;
      }

      return false;
    },
    /**
     * Focuses the item at the given index and scrolls to it
     *
     * @param {number} itemIndex - index of the item to focus
     * @returns {boolean} if the item was focused
     */
    focusItem(itemIndex) {
      if (this.$refs[`cartItem_${itemIndex}`] && this.$refs[`cartItem_${itemIndex}`][0]) {
        this.$refs[`cartItem_${itemIndex}`][0].focus({ preventScroll: true });
        this.$refs[`cartItem_${itemIndex}`][0].scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
        return true;
      }

      return false;
    },
  },
};
</script>
